import { Divider, Stack, Typography } from '@mui/material';

import { HelpIcon } from './helpIcon';

import { CopyToClipboardButton } from '../buttons/copyToClipboardButton';

export function CopyableCode({
  title,
  code,
  codeClassName,
  variant,
  tooltipTitle,
  maxHeight,
  blur = false,
}: {
  code: string;
  codeClassName?: string;
  title?: string;
  variant?: 'default' | 'error';
  tooltipTitle?: React.ReactNode;
  maxHeight?: string;
  blur?: boolean;
}) {
  const color =
    variant === 'error'
      ? 'var(--mui-palette-error-main)'
      : 'var(--mui-palette-text-primary)';
  const bgColorClass =
    variant === 'error'
      ? 'bg-(--mui-palette-error-opacity)'
      : 'bg-(--mui-palette-background-secondary)';

  if (title) {
    return (
      <Stack
        className={`w-full p-[16px] ${bgColorClass} rounded-[4px]`}
        divider={
          <Divider
            sx={{
              borderStyle: 'dashed',
              borderColor: color,
            }}
          />
        }
        sx={{
          gap: '12px',
          maxHeight,
        }}
      >
        <Stack
          direction='row'
          sx={{
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant='subtitle1Strong'
            sx={{
              color,
            }}
          >
            {title}
          </Typography>
          <Stack
            direction='row'
            sx={{
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {tooltipTitle && (
              <HelpIcon
                alwaysVisible
                title={tooltipTitle}
                className='text-xl'
                tooltipProps={{ disableInteractive: false }}
              />
            )}
            <CopyToClipboardButton
              textToCopy={blur ? '' : code}
              disabledTooltip={
                blur ? 'Fill all required fields before copy' : undefined
              }
              className={`text-[${color}]`}
            />
          </Stack>
        </Stack>
        <CodeBlock
          code={code}
          className={codeClassName}
          color={color}
          blur={blur}
        />
      </Stack>
    );
  }

  return (
    <Stack
      direction='row'
      className={`w-full p-[16px] ${bgColorClass} rounded-[4px]`}
      sx={{
        gap: '8px',
        alignItems: 'start',
        maxHeight,
      }}
    >
      <CodeBlock
        code={code}
        className={codeClassName}
        color={color}
        blur={blur}
      />
      <CopyToClipboardButton
        textToCopy={blur ? '' : code}
        className={`text-[${color}]`}
      />
    </Stack>
  );
}

const CodeBlock = ({
  code,
  className,
  color,
  blur = false,
}: {
  code: string;
  className?: string;
  color: string;
  blur?: boolean;
}) => {
  return (
    <code
      className={` break-pretty w-full whitespace-pre overflow-x-auto text-[13px] text-[${
        color
      }] ${className ?? ''}`}
    >
      {blur ? (
        <span
          className='select-none pointer-events-none'
          style={{
            userSelect: 'none',
            color: 'var(--mui-palette-text-disabled)',
          }}
        >
          {code}
        </span>
      ) : (
        code
      )}
    </code>
  );
};
